import { ModalType, useUIContext } from '../../../../context/UIContext'
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import AvatarEgg from '../../../Misc/AvatarEgg'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { LogoOrganization } from '../../../LogoOrganization'
import { useRankingCourses } from '../../../../pages/ranking/hooks/useRankingCourses'
import { useToken } from '../../../../hooks/useToken'
import { useTranslation } from 'react-i18next'
import { useUser } from '../../../../hooks/queries/useUser'

export const useNavBarItems = () => {
    const { t } = useTranslation('navbar')
    const navigate = useNavigate()
    const { pathname } = useLocation()
    const { _classId: _courseId } = useToken()
    const { dispatchModal } = useUIContext()
    const { user, displayNameOrName } = useUser()
    const [activeItem, setActiveItem] = useState<ItemMenuProps>()
    const [visibleMenu, setVisibleMenu] = useState(false)
    const [visibleIndexContent, setVisibleIndexContent] = useState(false)
    const { rankingCourses } = useRankingCourses()

    const states = useMemo(
        () => ({
            activeItem,
            setActiveItem,
            visibleMenu,
            setVisibleMenu,
            visibleIndexContent,
            setVisibleIndexContent,
        }),
        [activeItem, visibleMenu, visibleIndexContent]
    )

    const isActive = useCallback(
        (label: string, url?: string) => activeItem?.label === label || (!!url && !!pathname.includes(url)),
        [activeItem, pathname]
    )

    const courseId = _courseId || rankingCourses?.[0]?._courseId

    const topMenu: MenuProps[] = [
        {
            logo: <LogoOrganization size="small" />,
            pageName: t('Home'),
            url: '/home',
        },
        {
            icon: ['far', 'circle-nodes'],
            activeIcon: ['fas', 'circle-nodes'],
            pageName: t('Ecosystem', {
                ns: 'ecosystem',
            }),
            url: '/ecosystem',
            hidden: user?.hideMarketplace,
        },
        {
            icon: ['far', 'bolt'],
            activeIcon: ['fas', 'bolt'],
            pageName: 'Ranking',
            url: '/ranking',
            onClick: () => navigate(`/ranking/${courseId}`),
        },
    ]

    const bottomMenu: MenuProps[] = [
        {
            icon: ['far', 'users'],
            activeIcon: ['fas', 'users'],
            pageName: t('My profile'),
            url: '/me',
            activeSvgIcon: <AvatarEgg size={{ mobile: 25, desktop: 30 }} title={displayNameOrName} />,
            svgIcon: <AvatarEgg size={{ mobile: 25, desktop: 30 }} title={displayNameOrName} />,
        },
        {
            icon: ['far', 'arrow-right-from-bracket'],
            activeIcon: ['fas', 'arrow-right-from-bracket'],
            pageName: t('Log out'),
            onClick: () => dispatchModal({ type: ModalType.CLOSE_SESSION }),
            neverActiveMobile: true,
        },
    ]

    const menuItems = (menu: MenuProps[]): ItemMenuProps[] =>
        menu.map(({ pageName, url, onClick, ...rest }) => {
            return {
                label: pageName,
                onClick: onClick ?? (url ? () => navigate(url) : undefined),
                isActive: isActive(pageName, url),
                url,
                ...rest,
            }
        })

    useEffect(() => {
        const allMenuItems = [
            ...menuItems(topMenu),
            ...menuItems(bottomMenu),
            // ...menuItems(courseMenu),
        ]
        allMenuItems.forEach(item => {
            if (item.url?.includes(pathname)) {
                setActiveItem(item)
            }
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname])

    return {
        topMenuItems: menuItems(topMenu),
        bottomMenuItems: menuItems(bottomMenu),
        // courseMenuItems: isInCourse ? menuItems(courseMenu) : [],
        // courseItem: isInCourse ? menuItems(courseMenu)[1] : undefined,
        states,
    }
}

export default useNavBarItems

interface MenuBaseProps {
    logo?: ReactNode
    activeIcon?: IconProp
    icon?: IconProp
    children?: ReactNode
    hidden?: boolean
    svgIcon?: ReactNode
    activeSvgIcon?: ReactNode
    onClick?: () => void
    neverActiveMobile?: boolean
    url?: string
}

export type ItemMenuProps = MenuBaseProps & {
    label: string
    isActive: boolean
}

type MenuProps = MenuBaseProps & {
    pageName: string
}
